<script setup>
/**
 * 「我的二维码」页，逐条对齐旧版 web/12.0195d2b4b240a93b0284.js 里的
 * app-qrcode 组件（类 b、模板工厂 w、样式 I）。旧版路由是 /tabs/recommend/qrcode。
 *
 * 旧版类 b：
 *   构造：qrdata = window.location.origin
 *         user   = authService.getUser()
 *   ngOnInit: qrdata = qrdata + '/register/recommend/' + user.username
 *
 * 模板：
 *   ion-header：ion-button[routerLink="/tabs/recommend"] + ion-title「我的二维码」
 *   ion-content[padding]
 *     div.qrcode > <qrcode level="H" [qrdata]="qrdata" size="256">
 *     ion-text > p.tip「 长按图片保存 」
 *
 * 样式 I：
 *   .qrcode{display:flex;align-items:center;justify-content:center}
 *   .tip{text-align:center}
 *
 * 关于二维码本身：二维码不是接口给的图片，是前端本地算出来的。
 * 旧版模板里的 <qrcode> 是 angularx-qrcode 的组件（模块 JJ7l），它内部 new 的是
 * qrcodejs（模块 0ERz），而 0ERz 被整个打进了 main.js —— 所以旧版自带这个能力。
 * 新版 web-vue 的 package.json 里只有 vue + vue-router，没有任何 QR 库。
 *
 * 这里的做法是把旧版 main.js 里的 0ERz 模块原样抽出来放到
 * src/vendor/qrcodejs.js，直接复用：不用新增依赖，而且同一个库、同一套参数，
 * 画出来的二维码和旧版逐点一致。
 *
 * 旧版 QRCodeComponent 传给 qrcodejs 的参数（模块 JJ7l 的 ngOnInit 原文）：
 *   new QRCode(el, {
 *     colorDark: '#000000',        // colordark 默认值
 *     colorLight: '#ffffff',       // colorlight 默认值
 *     correctLevel: QRCode.CorrectLevel['H'],   // 模板里 level="H"
 *     width: 256, height: 256,     // 模板里 size="256"
 *     text: qrdata || ' ',
 *     useSVG: false                // usesvg 默认 false → 走 canvas
 *   })
 * qrcodejs 画完 canvas 后还会 makeImage() 生成一个 <img> 顶上去，
 * 这也正是页面底下那句「长按图片保存」能成立的原因。
 */
import { onMounted, ref } from 'vue'
import { authStorage, request } from '../services/api'
import AppBottomNav from '../components/AppBottomNav.vue'
// 用动态 import：① 旧版这个库也是跟 chunk 12 一起懒加载的
// ② 它的顶层代码会在加载时就执行（读 document、装 canvas 补丁），
//    静态 import 会被并进主 bundle，一旦出错整站白屏；懒加载则只影响这一页

const qrdata = ref('')
// qrcodejs 直接往这个容器里插 canvas / img（旧版是插在 <qrcode> 元素里）
const holderRef = ref(null)
// 生成失败时才用到：旧版 catch 里只 console.error，页面会是空白，
// 这里额外把链接显示出来方便排查，正常情况下不会出现
const qrFailed = ref(false)

/** 旧版 qrdata = window.location.origin + '/register/recommend/' + user.username */
async function buildQrData() {
  let username = authStorage.getUserModel()?.username || ''
  if (!username) {
    // 本地没存用户名时兜底问一次接口（旧版是直接从 authService 的本地用户取的）
    try { username = (await request('/user/profile')).data?.username || '' } catch (_) { /* 忽略 */ }
  }
  qrdata.value = `${window.location.origin}/register/recommend/${username}`
}

/**
 * 旧版 <qrcode level="H" size="256">，参数逐条照抄 JJ7l 的 ngOnInit。
 * 旧版 catch 里是 console.error('Error generating QR Code: ' + t.message)。
 */
async function renderQrcode() {
  if (!qrdata.value || !holderRef.value) return
  try {
    const { default: QRCode } = await import('../vendor/qrcodejs')
    // 旧版 ngOnChanges 重画前会 clear()，这里首次渲染也先清空，避免重复插入
    holderRef.value.innerHTML = ''
    new QRCode(holderRef.value, {
      colorDark: '#000000',
      colorLight: '#ffffff',
      correctLevel: QRCode.CorrectLevel.H,
      width: 256,
      height: 256,
      text: qrdata.value || ' ',
      useSVG: false,
    })
    qrFailed.value = false
  } catch (error) {
    // 旧版原话
    console.error('Error generating QR Code: ' + error.message)
    qrFailed.value = true
  }
}

onMounted(async () => {
  await buildQrData()
  await renderQrcode()
})
</script>

<template>
  <div class="qrcode-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮 routerLink="/tabs/recommend" -->
    <header class="modal-header">
      <RouterLink class="modal-back" to="/user/recommend" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">我的二维码</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-content[padding] -->
    <div class="modal-content">
      <!-- 旧版 div.qrcode 里放的是 <qrcode level="H" size="256">，
           qrcodejs 会往那个元素里插 canvas 和 img，这里的 .qrcode-holder 就是它 -->
      <div class="qrcode">
        <div ref="holderRef" class="qrcode-holder"></div>
        <!-- 只在 qrcodejs 抛错时出现（旧版这种情况是空白页） -->
        <p v-if="qrFailed" class="qrcode-fallback">{{ qrdata }}</p>
      </div>

      <!-- 旧版 ion-text > p.tip。能「长按保存」是因为 qrcodejs 的 makeImage()
           在 canvas 之上生成了一个 <img> -->
      <div class="ion-text">
        <p class="tip"> 长按图片保存 </p>
      </div>
    </div>

    <!-- 旧版这一页的路由是 /tabs/recommend/qrcode，属于 tabs 的子路由，
         ion-tabs 的 tab 栏在子页面里一直挂着，所以这里也带底部导航 -->
    <AppBottomNav />
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致 */
/* padding-bottom 是给底部 tab 栏（fixed，56px）留位 */
.qrcode-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif;padding-bottom:56px;box-sizing:border-box}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}
/* ion-content[padding] → 四边 16px */
.modal-content{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px}

/* --- 以下逐条照抄旧版样式表 I（chunk 12 的 app-qrcode） --- */
.qrcode{display:flex;align-items:center;justify-content:center}
.tip{text-align:center}

/* qrcodejs 插进来的 canvas / img。旧版没写这条（<qrcode> 元素默认就是 inline），
   这里让容器不影响居中，并且窄屏下图片不溢出 */
.qrcode-holder{line-height:0}
.qrcode-holder :deep(img),.qrcode-holder :deep(canvas){max-width:100%;height:auto}

/* 只在 qrcodejs 抛错时才显示，旧版没有这一条 */
.qrcode-fallback{margin:24px 0;color:#666;font-size:.8rem;text-align:center;word-break:break-all}
</style>
